import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Upload, Layers, Wrench, ChevronRight, Activity, Database, Ship, Image, History, GitMerge } from 'lucide-react';

const Admin = () => {
  const tools = [
    {
      title: 'Itinerary Upload',
      description: 'Upload cruise itinerary CSV/JSON files and reconcile ship metadata.',
      to: '/admin/itinerary-upload',
      icon: <Upload className="h-6 w-6 text-teal-600" />,
      accent: 'from-teal-500/10 to-teal-500/0',
    },
    {
      title: 'Ship Class Normalization',
      description: 'Merge ship class variants across lines and assign normalized names.',
      to: '/admin/ship-class-normalization',
      icon: <Layers className="h-6 w-6 text-indigo-600" />,
      accent: 'from-indigo-500/10 to-indigo-500/0',
    },
    {
      title: 'Ship & Itinerary Manager',
      description: 'Rename ship names globally or mass delete itineraries by ship.',
      to: '/admin/ship-itinerary-manager',
      icon: <Ship className="h-6 w-6 text-blue-600" />,
      accent: 'from-blue-500/10 to-blue-500/0',
    },
    {
      title: 'Ship Photo Manager',
      description: 'View, detect, and map ship photos to Firestore metadata.',
      to: '/admin/photo-manager',
      icon: <Image className="h-6 w-6 text-emerald-600" />,
      accent: 'from-emerald-500/10 to-emerald-500/0',
    },
    {
      title: 'Import History & Webhook Logs',
      description: 'Review logs for scraper webhooks and manual itinerary uploads.',
      to: '/admin/imports',
      icon: <History className="h-6 w-6 text-fuchsia-600" />,
      accent: 'from-fuchsia-500/10 to-fuchsia-500/0',
    },
    {
      title: 'Itinerary Reconciliation',
      description: 'Reconcile manually entered user cruises with official itineraries.',
      to: '/admin/reconciliation',
      icon: <GitMerge className="h-6 w-6 text-indigo-600" />,
      accent: 'from-indigo-500/10 to-indigo-500/0',
    },
    {
      title: 'System Analytics',
      description: 'View platform usage, user base metrics, and cruise data insights.',
      to: '/admin/analytics',
      icon: <Activity className="h-6 w-6 text-indigo-600" />,
      accent: 'from-indigo-500/10 to-indigo-500/0',
    },
    {
      title: 'Data Integrity',
      description: 'Scan for duplicate users, orphaned cruises, unlinked cruises, and broken profiles.',
      to: '/admin/integrity',
      icon: <Database className="h-6 w-6 text-indigo-600" />,
      accent: 'from-indigo-500/10 to-indigo-500/0',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-5xl mx-auto"
    >
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Admin Portal</h1>
        <p className="text-gray-600 mt-1">Choose an admin tool to continue</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tools.map((tool) => (
          <Link key={tool.to} to={tool.to}>
            <motion.div
              whileHover={{ y: -2 }}
              className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${tool.accent}`} aria-hidden />
              <div className="relative flex items-start gap-4">
                <div className="p-3 bg-white/80 rounded-lg shadow-sm border border-gray-100">
                  {tool.icon}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    {tool.title}
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </h2>
                  <p className="text-gray-600 mt-1 text-sm">{tool.description}</p>
                </div>
              </div>
            </motion.div>
          </Link>
        ))}
      </div>

      <div className="mt-6 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800 flex items-start gap-3">
        <Wrench className="h-5 w-5 text-blue-500 mt-0.5" />
        <div>
          <p className="font-medium text-blue-900">Need a new admin tool?</p>
          <p className="text-blue-700">Reach out to the team if you’d like another workflow added here.</p>
        </div>
      </div>
    </motion.div>
  );
};

export default Admin;
